import db from "../../../../database";
import { v4 as uuidv4 } from "uuid";
import { syncSingleProductByValue } from "../../../../services";
import useAuthStore from "../../../../stores/useAuthStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { roundTo, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../utils/tillHelpers";

// TSK-5562: m_pricing_xproduct.price -- 0 (or missing on a till not yet re-synced) keeps the
// product free, as before; > 0 adds it at that nominal price.
// TSK-5562: name shown in the offer tag under a TDF offer line (see utils/pos/lineOfferTag.js).
const getTdfOfferName = (pricingRule) => pricingRule.printedName || pricingRule.name || "";

const getXProductPrice = (xProduct) => {
  const price = Number(xProduct?.price);
  return Number.isFinite(price) && price > 0 ? price : 0;
};

// TSK-5562: lines this rule added to the cart. Marked with isTdfProduct, not isFree: an isFree
// line goes through useCartHandlers' quantity cap (weight + qty <= quantity), which would block
// the add itself since productDefined already carries weight 1.
const isTdfLineOfRule = (item, pricingRule) => item?.isTdfProduct === true && item?.mPricingruleId === pricingRule.mPricingrulesId;

// Same nettotal/taxAmount maths as addProductToCart's new-line branch (cart.js), including how it
// resolves tax-inclusive vs tax-exclusive.
const getNominalLineAmounts = (product, unitPrice, qty, tillData) => {
  if (!(unitPrice > 0)) return { nettotal: 0, taxAmount: 0 };
  const precision = getPrecision(tillData);
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData?.tillAccess?.csBunit?.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  taxIncludeFlag = product?.taxFlag === "Y" ? "N" : taxIncludeFlag;
  const taxRate = Number(product?.taxRate) || 0;
  const mrp = unitPrice * qty;
  const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + taxRate / 100) : mrp * (taxRate / 100);
  return {
    nettotal: taxIncludeFlag === "Y" ? roundTo(mrp, precision) : roundTo(mrp + tax, precision),
    taxAmount: roundTo(tax, precision),
  };
};

// TSK-5562: products this rule offers at a nominal price. Lines of these never count towards the rule's
// own minimum bill amount -- otherwise a scanned Rs 110 atta could lift a Rs 1,950 bill over Rs 1,999,
// get repriced to Rs 99, drop the bill below again, revert to Rs 110, and so on. Free (price 0) products
// are left out of this, so their behaviour is unchanged.
const getNominalOfferProductIds = (pricingRule) =>
  new Set(pricingRule.mPricingXProducts.filter((x) => x.isFree === "Y" && getXProductPrice(x) > 0).map((x) => x.mProductId));

// TSK-5562: the X product is already in the cart (the cashier scanned it) -- charge the configured
// nominal price on up to the configured quantity of that line, the same way a free X product is turned
// to 0 in place. Any units beyond that quantity stay on a line at their own price. The line as it was is
// kept on the repriced one (tdfOriginalLine) so it can be put back if the bill stops qualifying.
// Returns true when it changed `items`.
const applyNominalPriceInPlace = (items, pricingRule, xProduct, xProductPrice, tillData) => {
  if (items.some((l) => l.productId === xProduct.mProductId && isTdfLineOfRule(l, pricingRule))) return false;
  const index = items.findIndex((l) => l.productId === xProduct.mProductId && !l.isReturn && l.isCanceled !== "Y" && Number(l.weight) > 0);
  if (index < 0) return false;
  const original = items[index];
  const lineQty = Number(original.weight);
  const configuredQty = Number(xProduct.quantity);
  const offerQty = configuredQty > 0 ? Math.min(configuredQty, lineQty) : lineQty;
  const precision = getPrecision(tillData);
  const offerLine = {
    ...original,
    salePrice: xProductPrice,
    realPrice: xProductPrice,
    listPrice: xProductPrice,
    sunitprice: xProductPrice,
    discount: 0,
    discountName: "",
    weight: offerQty,
    ...getNominalLineAmounts(original, xProductPrice, offerQty, tillData),
    isTdfProduct: true,
    tdfOfferName: getTdfOfferName(pricingRule),
    mPricingruleId: pricingRule.mPricingrulesId,
    tdfOriginalLine: { ...original, weight: offerQty, ...scaleLineAmounts(original, offerQty / lineQty, precision) },
  };
  if (offerQty < lineQty) {
    items[index] = { ...original, weight: lineQty - offerQty, ...scaleLineAmounts(original, (lineQty - offerQty) / lineQty, precision) };
    items.splice(index, 0, { ...offerLine, lineId: uuidv4().replace(/-/g, "").toUpperCase() });
  } else {
    items[index] = offerLine;
  }
  return true;
};

// TSK-5562: an offer line can grow past the rule's Quantity after the offer was applied -- "+" on the line,
// a typed qty or the weight popup all edit that same line (by lineId), at its offer price, so 2 units came
// out as 99 + 99. Put the offer line back to the configured quantity and move the extra units to a line of
// their own at the product's regular price -- the same result as scanning the product again (99 + 110).
// The regular price is the repriced line's own (tdfOriginalLine) or, for a line this rule added, the
// product's price stored when it was added (tdfRegularPrice). Without either (a line from an older build)
// the line is left as it is. Quantity empty / 0 = no limit, as before. Returns true when it changed `items`.
const capOfferLinesToConfiguredQty = (items, pricingRule, tillData) => {
  const precision = getPrecision(tillData);
  let changed = false;
  pricingRule.mPricingXProducts.forEach((xProduct) => {
    const configuredQty = Number(xProduct.quantity);
    if (xProduct.isFree !== "Y" || !(configuredQty > 0)) return;
    let allowance = configuredQty;
    for (let i = 0; i < items.length; i++) {
      const line = items[i];
      if (line.productId !== xProduct.mProductId || !isTdfLineOfRule(line, pricingRule) || line.isReturn || line.isCanceled === "Y") continue;
      const lineQty = Number(line.weight) || 0;
      if (lineQty <= allowance) {
        allowance -= lineQty;
        continue;
      }
      const extraQty = lineQty - allowance;
      const keepQty = allowance;
      const extraLine = buildRegularPriceLine(line, extraQty, tillData, precision);
      if (!extraLine) continue;
      allowance = 0;
      // Extra units go where adding the product from search would put them: onto its existing
      // regular-price line, else a new line.
      const mergeIndex = findSearchMergeLineIndex(items, line, extraLine.salePrice);
      if (keepQty > 0) {
        const offerPrice = Number(line.salePrice) || 0;
        items[i] = {
          ...line,
          weight: keepQty,
          ...getNominalLineAmounts(line, offerPrice, keepQty, tillData),
          ...(line.tdfOriginalLine
            ? { tdfOriginalLine: { ...line.tdfOriginalLine, weight: keepQty, ...scaleLineAmounts(line.tdfOriginalLine, keepQty / Number(line.tdfOriginalLine.weight), precision) } }
            : {}),
        };
        if (mergeIndex >= 0) {
          addUnitsToLine(items, mergeIndex, extraQty, tillData);
        } else {
          items.splice(i + 1, 0, extraLine);
          i += 1;
        }
      } else if (mergeIndex >= 0) {
        addUnitsToLine(items, mergeIndex, extraQty, tillData);
        items.splice(i, 1);
        i -= 1;
      } else {
        items[i] = { ...extraLine, lineId: line.lineId };
      }
      changed = true;
    }
  });
  return changed;
};

// The extra units of an offer line, as an ordinary line at the product's regular price (null = price unknown).
const buildRegularPriceLine = (line, qty, tillData, precision) => {
  const lineId = uuidv4().replace(/-/g, "").toUpperCase();
  const original = line.tdfOriginalLine;
  if (original && Number(original.weight) > 0) {
    return { ...original, lineId, weight: qty, ...scaleLineAmounts(original, qty / Number(original.weight), precision) };
  }
  const regularPrice = Number(line.tdfRegularPrice);
  if (!(regularPrice > 0)) return null;
  // eslint-disable-next-line no-unused-vars
  const { isTdfProduct, tdfOfferName, tdfOriginalLine, tdfRegularPrice, ...rest } = line;
  return {
    ...rest,
    lineId,
    weight: qty,
    salePrice: regularPrice,
    realPrice: regularPrice,
    listPrice: regularPrice,
    sunitprice: regularPrice,
    discount: 0,
    discountName: "",
    mPricingruleId: null,
    ...getNominalLineAmounts(line, regularPrice, qty, tillData),
  };
};

const scaleLineAmounts = (line, ratio, precision) => ({
  nettotal: roundTo((Number(line.nettotal) || 0) * ratio, precision),
  taxAmount: roundTo((Number(line.taxAmount) || 0) * ratio, precision),
  discount: roundTo((Number(line.discount) || 0) * ratio, precision),
});

// TSK-5562: undo applyNominalPriceInPlace for a line whose bill no longer qualifies -- back to its own
// price, keeping whatever quantity the line has now.
const restoreRepricedLine = (line, tillData) => {
  const original = line.tdfOriginalLine;
  const ratio = Number(original.weight) ? Number(line.weight) / Number(original.weight) : 1;
  return { ...original, lineId: line.lineId, weight: line.weight, ...scaleLineAmounts(original, ratio, getPrecision(tillData)) };
};

// TSK-5562 (speed): this runs on every cart action once a TDF rule applies. An exact key lookup is used
// first -- equalsIgnoreCase walks the index trying case variants, which is slow on a large catalogue (and
// over IPC on CW Browser). The case-insensitive lookup is kept as the fallback, so an id stored in another
// case still resolves exactly as before.
const findActiveProductsById = async (mProductId) => {
  let rows = await db.products.where("mProductId").equals(mProductId).toArray();
  if (rows.length === 0) rows = await db.products.where("mProductId").equalsIgnoreCase(mProductId).toArray();
  return rows.filter((p) => p.isactive == true);
};

// TSK-5562: the line that adding this product from search would add to (addProductToCart's same-product
// match: same product / UPC / batch / UOM, not a return, cancelled, fired, discounted, offer or add-on
// line), at the same regular price -- so "+" on an offer line ends up exactly like scanning the product
// again. -1 when there is none, or the product is set to Add New Line (search then always makes a new line).
const findSearchMergeLineIndex = (items, line, regularPrice) => {
  if (line.addNewLine === "Y") return -1;
  return items.findIndex(
    (p) =>
      p.productId === line.productId &&
      (p.upc || "") === (line.upc || "") &&
      p.mBatchId === line.mBatchId &&
      p.uom === line.uom &&
      !p.isReturn &&
      p.isCanceled !== "Y" &&
      p.order !== "Y" &&
      !p.isTdfProduct &&
      !p.isFree &&
      !p.discountType &&
      !p.discountName &&
      !(Math.abs(Number(p.discount) || 0) > 0) &&
      !(p.selectedAddons?.length > 0) &&
      Number(p.weight) > 0 &&
      Number(p.salePrice) === Number(regularPrice)
  );
};

// Adds `qty` units to the regular-price line at `index`, recomputing its total and tax.
const addUnitsToLine = (items, index, qty, tillData) => {
  const target = items[index];
  const weight = (Number(target.weight) || 0) + qty;
  items[index] = { ...target, weight, ...getNominalLineAmounts(target, Number(target.salePrice), weight, tillData) };
};

// TSK-5562 (speed): last offer list written through each setOfferProductList setter, so an unchanged list
// is not written again (every write re-renders the POS screen).
const lastOfferListKeys = new WeakMap();
const writeOfferList = (setOfferProductList, list) => {
  if (typeof setOfferProductList !== "function") return;
  const key = list.map((p) => `${p.productId}|${p.salePrice}`).join(",");
  if (lastOfferListKeys.get(setOfferProductList) === key) return;
  lastOfferListKeys.set(setOfferProductList, key);
  setOfferProductList([...list]);
};

export const TotalBillFreeProductDiscount = async (
  pricingRule,
  setCart,
  cart,
  orderType,
  cartObj,
  addProduct,
  setOfferProductList,
  setDisplayOfferProductSelection,
  iscoupon,
  couponInput,
  uniqReferenceId,
  mPricingCouponId,
  mPricingRulesId
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  const saleTypeValidation = pricingRule.cwrSaletypeId === null ? true : pricingRule.cwrSaletypeId === orderType.cwrSaletype.cwrSaletypeId;
  const billCart = { ...cartObj };
  const tillData = useAuthStore.getState().tillData;

  let categoryItems = [];
  let brandItems = [];
  billCart.items.map((item) => {
    const stateValidation = pricingRule.mPricingXProducts.some((pc) => pc.mProductId === item.productId);
    const brandValidation = pricingRule.mPricingBrands.some((pc) => pc.mBrandId === item.productBrandId);
    const categoryValidation = pricingRule.mPricingPcategories.some((pc) => pc.mProductCategoryId === item.mProductCategoryId);
    if (stateValidation) {
      brandItems.push(item);
    }
    if (brandValidation) {
      brandItems.push(item);
    }
    if (categoryValidation) {
      categoryItems.push(item);
    }
    return null;
  });

  let allItems = [...brandItems, ...categoryItems];
  let uniqueFinalArray = [...new Set(allItems)];
  let flag = brandItems.length > 0 ? true : categoryItems.length > 0 ? true : false;

  if (saleTypeValidation) {
    // TSK-5562: neither this rule's own lines nor its nominal-price offer products count towards its
    // threshold (Rs 1,950 bill + Rs 99 sugar would otherwise keep clearing a Rs 1,999 minimum). Free
    // lines are 0, so this changes nothing for them.
    const nominalOfferProductIds = getNominalOfferProductIds(pricingRule);
    const isOutsideThreshold = (item) => isTdfLineOfRule(item, pricingRule) || nominalOfferProductIds.has(item.productId);
    const tdfLinesTotal = cartObj.items.reduce((sum, item) => (isOutsideThreshold(item) ? sum + (Number(item.nettotal) || 0) : sum), 0);
    const qualifyingBillTotal = cartObj.total - tdfLinesTotal;
    let maxBillAmountFlag = pricingRule.maxBillAmount ? qualifyingBillTotal <= pricingRule.maxBillAmount : true;
    const minQtyFlag = pricingRule.minimumQty ? cartObj.totalQty >= pricingRule.minimumQty : true;
    const maxQtyFlag = pricingRule.maximumQty ? cartObj.totalQty <= pricingRule.maximumQty : true;
    const minAmountFlag = pricingRule.billAmount ? qualifyingBillTotal >= pricingRule.billAmount : true;
    const maxAmountFlag = pricingRule.maxBillAmount ? qualifyingBillTotal <= pricingRule.maxBillAmount : true;
    let totalPrice = 0;
    let excludingFlag = true;

    if (pricingRule.mPricingB2CCustomerSegments.length > 0) {
      const matchingCustomerSegment = pricingRule.mPricingB2CCustomerSegments.find(
        (pc) => pc.cwrB2CCustomerSegmentId === cartObj.customer?.b2cCustomerSegment?.cwrB2CCustomerSegmentId
      );

      if (
        (pricingRule.excludeB2CSegment === "Y" && matchingCustomerSegment) ||
        (pricingRule.excludeB2CSegment !== "Y" && !matchingCustomerSegment && pricingRule.mPricingB2CCustomerSegments.length > 0) ||
        (pricingRule.excludeB2CSegment === "N" && pricingRule.mPricingB2CCustomerSegments.length === 0)
      ) {
        excludingFlag = false;
      }
    }

    if (pricingRule.mPricingB2CCustomers.length > 0) {
      const matchingCustomer = pricingRule.mPricingB2CCustomers.find((pc) => pc.b2cCustomerId === cartObj.customer.cwrCustomerId);

      if (
        (pricingRule.excludeB2CCustomers === "Y" && matchingCustomer) ||
        (pricingRule.excludeB2CCustomers !== "Y" && !matchingCustomer && pricingRule.mPricingB2CCustomers.length > 0) ||
        (pricingRule.excludeB2CCustomers === "N" && pricingRule.mPricingB2CCustomers.length === 0)
      ) {
        excludingFlag = false;
      }
    }
    cartObj.items.map((product) => (totalPrice = isOutsideThreshold(product) ? totalPrice : totalPrice + product.nettotal));
    // TSK-5562: Free = N X products are what the customer has to buy. When the rule lists any, at least
    // one of them (any one is enough) must be on the bill before the Free = Y product is given -- free or
    // at its configured price alike. Rules with no Free = N rows are unaffected.
    const requiredProductIds = pricingRule.mPricingXProducts.filter((x) => x.isFree === "N").map((x) => x.mProductId);
    const requiredProductFlag =
      requiredProductIds.length === 0 ||
      cartObj.items.some(
        (item) =>
          requiredProductIds.includes(item.productId) && item.isCanceled !== "Y" && Number(item.weight) > 0 && !item.isReturn && !isTdfLineOfRule(item, pricingRule)
      );
    // Writes `items` back as the cart (or exchange cart) with its totals recomputed.
    const commitItems = (baseCart, items) => {
      let totalTax = 0;
      let totalPriceOfItems = 0;
      let totalItemsQty = 0;
      let totalDiscounts = 0;
      items.forEach((item, i) => {
        item.key = i;
        if (item?.isCanceled === "Y" || item?.weight === 0) return;
        totalPriceOfItems += item.nettotal;
        totalItemsQty += item.weight;
        totalTax += item.taxAmount;
        totalDiscounts += item.discount || 0;
      });
      const { roundedTotal, roundOffAmount } = applyCurrencyRounding(totalPriceOfItems, getRoundingMethod(tillData), getPrecision(tillData));
      const committed = {
        ...baseCart,
        items,
        total: roundedTotal,
        tax: roundTo(totalTax, getPrecision(tillData)),
        discount: totalDiscounts,
        totalQty: totalItemsQty,
        roundOff: roundOffAmount,
      };
      if (isExchange) {
        useCartStore.getState().setExchangeCart({ ...committed });
      } else {
        setCart(committed);
      }
      return committed;
    };
    if (totalPrice >= pricingRule.billAmount && maxBillAmountFlag && minQtyFlag && maxQtyFlag && minAmountFlag && maxAmountFlag && requiredProductFlag) {
      // TSK-5562: first hold every offer line to the rule's Quantity (extra units -> regular-price line),
      // saved right away so both paths below start from the corrected cart.
      const cappedItems = cartObj.items.map((item) => ({ ...item }));
      if (capOfferLinesToConfiguredQty(cappedItems, pricingRule, tillData)) {
        cartObj = commitItems(cartObj, cappedItems);
      }
      if (pricingRule.mPricingXProducts.length > 0) {
        if (pricingRule.mPricingXProducts.length === 1) {
          await findActiveProductsById(pricingRule.mPricingXProducts[0].mProductId)
            .then(async (product) => {
              // Out-of-stock free product absent from local DB → fetch on-demand,
              // then re-read (mirrors the return-flow getProductData fallback).
              // Guarded by length === 0 so the in-stock path is unchanged.
              if (product.length === 0 && pricingRule.mPricingXProducts[0]?.mProductId) {
                try {
                  const tillData = useAuthStore.getState().tillData;
                  const syncId = uuidv4().replace(/-/g, "").toUpperCase();
                  const synced = await syncSingleProductByValue(tillData, syncId, tillData?.tillAccess?.cwrTill?.cwrTillID, pricingRule.mPricingXProducts[0].mProductId.toString().trim());
                  if (synced) {
                    product = await findActiveProductsById(pricingRule.mPricingXProducts[0].mProductId);
                  }
                } catch (err) {
                  console.error("Free-product on-demand sync failed:", err);
                }
              }
              if (product.length > 0) {
                const obj = { ...product[0] };
                let batchno = null;
                let mBatchId = null;
                let upc = null;
                if (obj.batchedProduct === "Y" && obj.batchedForSale === "Y") {
                  const bI = (obj.mBatch || []).findIndex((p) => p.mBatchId === pricingRule.mPricingXProducts[0].mBatchId);
                  if (bI >= 0) {
                    batchno = obj.mBatch[bI].batchno;
                    mBatchId = obj.mBatch[bI].mBatchId;
                    upc = obj.mBatch[bI].upc;
                  }
                }
                if (obj.overRideTax === "Y" && 0 <= obj.overRideCondition) {
                  obj.cTaxId = obj.contraTaxId;
                  obj.taxRate = obj.contraRate;
                }
                const xProductPrice = getXProductPrice(pricingRule.mPricingXProducts[0]);
                const xProductAmounts = getNominalLineAmounts(obj, xProductPrice, 1, tillData);
                const productDefined = {
                  batchno: batchno,
                  description: obj.description,
                  discount: 0,
                  discountName: "",
                  lineId: uuidv4().replace(/-/g, "").toUpperCase(),
                  imageurl: obj.imageurl,
                  isDecimal: obj.isDecimal,
                  isManualQty: obj.isManualQty,
                  restrictPosQtyEdit: obj.restrictPosQtyEdit || "N",
                  showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
                  interTaxId: obj?.interTaxId || "",
                  interTaxRate: obj?.interTaxRate || 0,
                  interTaxName: obj?.interTaxName || "",
                  alternateUom: obj?.alternateUom || null,
                  companion: obj?.companion || "N",
                  isPromoApplicable: false,
                  isReturn: false,
                  isTdfProduct: true,
                  tdfOfferName: getTdfOfferName(pricingRule),
                  // TSK-5562: the product's own price, for units beyond the rule's Quantity (capOfferLinesToConfiguredQty)
                  tdfRegularPrice: Number(obj.sunitprice) || 0,
                  // Same default as search (useProductOperations): Add New Line products always get a new line.
                  addNewLine: obj.addNewLine || "N",
                  mBatchId: mBatchId,
                  mPricingruleId: pricingRule.mPricingrulesId,
                  name: obj.name,
                  nettotal: xProductAmounts.nettotal,
                  primaryOrderLine: null,
                  productId: obj.mProductId,
                  realPrice: xProductPrice,
                  listPrice: xProductPrice,
                  returnQty: null,
                  salePrice: xProductPrice,
                  sunitprice: xProductPrice,
                  stock: obj.onhandQty,
                  tax: obj.cTaxId,
                  taxAmount: xProductAmounts.taxAmount,
                  taxRate: obj.taxRate,
                  uom: obj.csUomId,
                  uom_name: obj.uomName,
                  isDecimalQty: obj.uomData?.[0]?.decimal === "Y" ? true : false,
                  isQtyDesimal: obj.uomData?.[0]?.stdprecision ?? 2,
                  upc: upc,
                  value: obj.value,
                  weight: 1,
                  shortDescription: obj.shortDescription,
                  hsncode: obj.hsncode,
                  csBunitId: obj.csBunitId,
                  mProductCategoryId: obj.mProductCategoryId,
                  productManufacturerId: obj.productManufacturerId,
                  productBrandId: obj.productBrandId,
                  productBrandName: obj?.productBrandName,
                  batchedProduct: obj.batchedProduct,
                  batchedForSale: obj.batchedForSale,
                  batchedForStock: obj.batchedForStock,
                  multiPrice: obj.multiPrice,
                  shelfLife: obj.shelfLife,
                };
                let productIndex = cartObj.items.findIndex((pc) => pc.productId === obj.mProductId);
                const matchingProductIndex = pricingRule.mPricingXProducts.findIndex((op) => op.mProductId === obj.mProductId);

                if (
                  (pricingRule.excludeProducts === "Y" && matchingProductIndex !== -1) ||
                  (pricingRule.excludeProducts !== "Y" && matchingProductIndex === -1 && pricingRule.mPricingXProducts.length > 0) ||
                  (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
                ) {
                  excludingFlag = false;
                }

                const matchingBrand = pricingRule.mPricingBrands.find((pc) => pc.mBrandId === obj.productBrandId);

                if (
                  (pricingRule.excludeBrands === "Y" && matchingBrand) ||
                  (pricingRule.excludeBrands !== "Y" && !matchingBrand && pricingRule.mPricingBrands.length > 0) ||
                  (pricingRule.excludeBrands === "N" && pricingRule.mPricingBrands.length === 0)
                ) {
                  excludingFlag = false;
                }

                const matchingBusinessUnit = pricingRule.mPricingBUnits.find((pc) => pc.mBunitPricingId === tillData.tillAccess.csBunit.csBunitId);
                if (
                  (pricingRule.excludeBusinessUnits === "Y" && matchingBusinessUnit) ||
                  (pricingRule.excludeBusinessUnits !== "Y" && !matchingBusinessUnit && pricingRule.mPricingBUnits.length > 0) ||
                  (pricingRule.excludeBusinessUnits === "N" && pricingRule.mPricingBUnits.length === 0)
                ) {
                  excludingFlag = false;
                }

                const matchingCategoy = pricingRule.mPricingPcategories.find((pc) => pc.mProductCategoryId === obj.mProductCategoryId);

                if (
                  (pricingRule.excludeProductCategories === "Y" && matchingCategoy) ||
                  (pricingRule.excludeProductCategories !== "Y" && !matchingCategoy && pricingRule.mPricingPcategories.length > 0) ||
                  (pricingRule.excludeProductCategories === "N" && pricingRule.mPricingPcategories.length === 0)
                ) {
                  excludingFlag = false;
                }

                if (productIndex < 0 && excludingFlag) {
                  addProduct(productDefined, 1);
                } else if (productIndex >= 0 && excludingFlag && xProductPrice > 0) {
                  // TSK-5562: already in the cart (scanned) -> reprice it to the nominal price. A free
                  // product already in the cart is left as it was (unchanged behaviour on this path).
                  const items = cartObj.items.map((item) => ({ ...item }));
                  if (applyNominalPriceInPlace(items, pricingRule, pricingRule.mPricingXProducts[0], xProductPrice, tillData)) {
                    return commitItems(cartObj, items);
                  }
                }
              }
            });
        } else {
          // TSK-5562: build on the cart the caller evaluated (cartObj), not the `cart` param -- that is the
          // hook's render-time copy (and the main cart even during an exchange), which can be missing the
          // item just added; writing it back below would drop that item from the bill.
          if (Array.isArray(cartObj?.items)) cart = cartObj;
          const offerProductsStack = [];
          const updatedItems = cart.items.map(item => ({ ...item }));
          // TSK-5562 (speed): set when this run changes a line; with no change, an already tagged cart is
          // not written again.
          let itemsChanged = false;
          for (let i = 0; i < pricingRule.mPricingXProducts.length; i++) {
            // TSK-5562 (speed): a Free = N row is never priced or offered (only its presence on the bill counts,
            // checked above) -- no product lookup, and no on-demand server sync, for it.
            if (pricingRule.mPricingXProducts[i].isFree !== "Y") continue;
            await findActiveProductsById(pricingRule.mPricingXProducts[i].mProductId)
              .then(async (product) => {
                // Out-of-stock free product absent from local DB → fetch on-demand,
                // then re-read. Guarded by length === 0 so in-stock path is unchanged.
                if (product.length === 0 && pricingRule.mPricingXProducts[i]?.mProductId) {
                  try {
                    const tillData = useAuthStore.getState().tillData;
                    const syncId = uuidv4().replace(/-/g, "").toUpperCase();
                    const synced = await syncSingleProductByValue(tillData, syncId, tillData?.tillAccess?.cwrTill?.cwrTillID, pricingRule.mPricingXProducts[i].mProductId.toString().trim());
                    if (synced) {
                      product = await findActiveProductsById(pricingRule.mPricingXProducts[i].mProductId);
                    }
                  } catch (err) {
                    console.error("Free-product on-demand sync failed:", err);
                  }
                }
                if (product.length > 0 && pricingRule.mPricingXProducts[i].isFree === "Y") {
                  const obj = { ...product[0] };
                  let batchno = null;
                  let mBatchId = null;
                  let upc = null;
                  if (obj.batchedProduct === "Y" && obj.batchedForSale === "Y") {
                    const bI = obj.mBatch.findIndex((p) => p.mBatchId === pricingRule.mPricingXProducts[i].mBatchId);
                    if (bI >= 0) {
                      batchno = obj.mBatch[bI].batchno;
                      mBatchId = obj.mBatch[bI].mBatchId;
                      upc = obj.mBatch[bI].upc;
                    }
                  }
                  if (obj.overRideTax === "Y" && 0 <= obj.overRideCondition) {
                    obj.cTaxId = obj.contraTaxId;
                    obj.taxRate = obj.contraRate;
                  }
                  const xProductPrice = getXProductPrice(pricingRule.mPricingXProducts[i]);
                  const xProductAmounts = getNominalLineAmounts(obj, xProductPrice, 1, tillData);
                  const productDefined = {
                    batchno: batchno,
                    description: obj.description,
                    lineId: uuidv4().replace(/-/g, "").toUpperCase(),
                    discount: 0,
                    discountName: "",
                    imageurl: obj.imageurl,
                    isDecimal: obj.isDecimal,
                    isManualQty: obj.isManualQty,
                    restrictPosQtyEdit: obj.restrictPosQtyEdit || "N",
                    showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
                    interTaxId: obj?.interTaxId || "",
                    interTaxRate: obj?.interTaxRate || 0,
                    interTaxName: obj?.interTaxName || "",
                    alternateUom: obj?.alternateUom || null,
                    companion: obj?.companion || "N",
                    isReturn: false,
                    isPromoApplicable: false,
                    isTdfProduct: true,
                    tdfOfferName: getTdfOfferName(pricingRule),
                    // TSK-5562: the product's own price, for units beyond the rule's Quantity (capOfferLinesToConfiguredQty)
                    tdfRegularPrice: Number(obj.sunitprice) || 0,
                    // Same default as search (useProductOperations): Add New Line products always get a new line.
                    addNewLine: obj.addNewLine || "N",
                    mBatchId: mBatchId,
                    name: obj.name,
                    nettotal: xProductAmounts.nettotal,
                    primaryOrderLine: null,
                    productId: obj.mProductId,
                    realPrice: xProductPrice,
                    listPrice: xProductPrice,
                    returnQty: null,
                    salePrice: xProductPrice,
                    sunitprice: xProductPrice,
                    stock: obj.onhandQty,
                    tax: obj.cTaxId,
                    taxAmount: xProductAmounts.taxAmount,
                    taxRate: obj.taxRate,
                    uom: obj.csUomId,
                    uom_name: obj.uomName,
                    isDecimalQty: obj.uomData?.[0]?.decimal === "Y" ? true : false,
                    isQtyDesimal: obj.uomData?.[0]?.stdprecision ?? 2,
                    upc: upc,
                    value: obj.value,
                    weight: 1,
                    shortDescription: obj.shortDescription,
                    hsncode: obj.hsncode,
                    csBunitId: obj.csBunitId,
                    mProductCategoryId: obj.mProductCategoryId,
                    productManufacturerId: obj.productManufacturerId,
                    productBrandId: obj.productBrandId,
                    productBrandName: obj?.productBrandName,
                    batchedProduct: obj.batchedProduct,
                    batchedForSale: obj.batchedForSale,
                    batchedForStock: obj.batchedForStock,
                    multiPrice: obj.multiPrice,
                    shelfLife: obj.shelfLife,
                    iscoupon: iscoupon,
                    mPricingruleId: pricingRule.mPricingrulesId,
                  };
                  // Index into updatedItems, which is what it is applied to below.
                  let productIndex = updatedItems.findIndex((pc) => pc.productId === obj.mProductId);
                  const matchingProductIndex = pricingRule.mPricingXProducts.findIndex((op) => op.mProductId === obj.mProductId);

                  if (
                    (pricingRule.excludeProducts === "Y" && matchingProductIndex !== -1) ||
                    (pricingRule.excludeProducts !== "Y" && matchingProductIndex === -1 && pricingRule.mPricingXProducts.length > 0) ||
                  (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
                  ) {
                    excludingFlag = false;
                  }

                  const matchingBrand = pricingRule.mPricingBrands.find((pc) => pc.mBrandId === obj.productBrandId);

                  if (
                  (pricingRule.excludeBrands === "Y" && matchingBrand) ||
                  (pricingRule.excludeBrands !== "Y" && !matchingBrand && pricingRule.mPricingBrands.length > 0) ||
                  (pricingRule.excludeBrands === "N" && pricingRule.mPricingBrands.length === 0)
                ) {
                    excludingFlag = false;
                  }

                  const matchingBusinessUnit = pricingRule.mPricingBUnits.find((pc) => pc.mBunitPricingId === tillData.tillAccess.csBunit.csBunitId);
                  if (
                    (pricingRule.excludeBusinessUnits === "Y" && matchingBusinessUnit) ||
                    (pricingRule.excludeBusinessUnits !== "Y" && !matchingBusinessUnit && pricingRule.mPricingBUnits.length > 0) ||
                  (pricingRule.excludeBusinessUnits === "N" && pricingRule.mPricingBUnits.length === 0)
                  ) {
                    excludingFlag = false;
                  }

                  const matchingCategoy = pricingRule.mPricingPcategories.find((pc) => pc.mProductCategoryId === obj.mProductCategoryId);

                  if (
                    (pricingRule.excludeProductCategories === "Y" && matchingCategoy) ||
                    (pricingRule.excludeProductCategories !== "Y" && !matchingCategoy && pricingRule.mPricingPcategories.length > 0) ||
                  (pricingRule.excludeProductCategories === "N" && pricingRule.mPricingPcategories.length === 0)
                  ) {
                    excludingFlag = false;
                  }

                  if (excludingFlag) {
                    if (productIndex >= 0) {
                      // TSK-5562: a free product is converted to 0 in place, as before (not marked
                      // isTdfProduct, so it is never removed as a TDF line). A nominal-price product is
                      // repriced in place on up to the configured quantity, and put back if the bill
                      // stops qualifying.
                      if (xProductPrice === 0) {
                        // Already free from an earlier run: leave it (and its lineId) as it is.
                        const current = updatedItems[productIndex];
                        if (!(current?.isFree === true && Number(current.salePrice) === 0 && Number(current.nettotal) === 0)) {
                          let weight = updatedItems[productIndex]?.weight;
                          updatedItems[productIndex] = { ...productDefined, weight, isFree: true };
                          updatedItems[productIndex].isTdfProduct = false;
                          itemsChanged = true;
                        }
                      } else if (applyNominalPriceInPlace(updatedItems, pricingRule, pricingRule.mPricingXProducts[i], xProductPrice, tillData)) {
                        itemsChanged = true;
                      }
                    } else {
                      offerProductsStack.push(productDefined);
                    }
                  }
                }
              });
          }

          // setTimeout(() => {
          let totalTax = 0;
          let totalPrice = 0;
          let totalItemsQty = 0;
          let totalDiscounts = 0;
          for (let i = 0; i < updatedItems.length; i += 1) {
            totalPrice += updatedItems[i].nettotal;
            totalItemsQty += updatedItems[i].weight;
            totalTax += updatedItems[i].taxAmount;
            totalDiscounts += updatedItems[i].discount;
            updatedItems[i].key = i;
          }

          const { roundedTotal: roundOffValue, roundOffAmount: totalRoundOff } = applyCurrencyRounding(totalPrice, getRoundingMethod(tillData), getPrecision(tillData));

          let couponsData = (cart.couponInput || []).length > 0 ? [...cart.couponInput] : [];
          let couponAdded = false;
          if (offerProductsStack.length > 0) {
            if (!couponsData.some((coupon) => coupon.couponCode === couponInput) && couponInput !== undefined && couponInput !== null) {
              couponsData.push({ couponCode: couponInput, referenceId: uniqReferenceId, mPricingCouponId: mPricingCouponId, mPricingruleId: mPricingRulesId });
              couponAdded = true;
            }
          }
          writeOfferList(setOfferProductList, offerProductsStack);
          // TSK-5562 (speed): nothing changed and the cart already carries this rule -> no write. Every
          // write re-renders the POS and re-saves the whole cart, and this runs on each cart action.
          if (
            !itemsChanged &&
            !couponAdded &&
            cart.totalBillDicount === pricingRule.mPricingrulesId &&
            cart.mPricingrulesId === pricingRule.mPricingrulesId &&
            cart.discountType === "TDF"
          ) {
            return cart;
          }
          const updatedCart = {
            ...cart,
            items: updatedItems,
            couponInput: couponsData,
            iscoupon: cart.iscoupon ? cart.iscoupon : false,
            mPricingrulesId: pricingRule.mPricingrulesId,
            totalBillDicount: pricingRule.mPricingrulesId,
            discountType: "TDF",
            total: roundOffValue,
            tax: roundTo(totalTax, getPrecision(tillData)),
            discount: totalDiscounts,
            totalQty: totalItemsQty,
            roundOff: totalRoundOff,
            saleType: usePOSConfigStore.getState().saleTypeData?.cwrSaletype || {},
          };
          if (isExchange) {
            useCartStore.getState().setExchangeCart({ ...updatedCart });
          } else {
            setCart(updatedCart);
          }

          return updatedCart;
        }
      }
    } else if (cartObj.items.some((item) => isTdfLineOfRule(item, pricingRule))) {
      // TSK-5562: the bill no longer qualifies -- drop the X product lines this rule added, and put lines
      // it repriced in place back to their own price, so the offer doesn't stay on a bill below the minimum.
      const remainingItems = cartObj.items.flatMap((item) => {
        if (!isTdfLineOfRule(item, pricingRule)) return [{ ...item }];
        return item.tdfOriginalLine ? [restoreRepricedLine(item, tillData)] : [];
      });
      return commitItems(cartObj, remainingItems);
    }
  }
};
